import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Loader2 } from 'lucide-react';
import { DateInput } from '@/components/ui/date-input';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const TYPES = [
  ['sales', 'Sales Incentive'],
  ['delivery', 'Delivery Incentive'],
  ['google_review', 'Google Review Incentive'],
  ['manual', 'Manual Rewards'],
  ['all', 'All Incentives'],
];

export const EmployeeIncentiveSettingsDialog = ({ employee, open, onOpenChange }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [audit, setAudit] = useState([]);
  const [form, setForm] = useState({ incentive_enabled: false, eligible_incentive_types: [], incentive_effective_from: '', incentive_effective_to: '', incentive_notes: '', change_notes: '' });

  const load = useCallback(async () => {
    if (!employee) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const [s, a] = await Promise.all([
        axios.get(`${API}/sales/employees/${employee.id}/incentive-settings`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/sales/employees/${employee.id}/incentive-audit`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
      ]);
      setSettings(s.data);
      setAudit(a.data || []);
      setForm({
        incentive_enabled: !!s.data.incentive_enabled,
        eligible_incentive_types: s.data.eligible_incentive_types || [],
        incentive_effective_from: (s.data.incentive_effective_from || '').slice(0, 10),
        incentive_effective_to: (s.data.incentive_effective_to || '').slice(0, 10),
        incentive_notes: s.data.incentive_notes || '',
        change_notes: '',
      });
    } catch (e) {
      toast.error('Failed to load incentive settings');
    } finally {
      setLoading(false);
    }
  }, [employee]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const toggleType = (t) => {
    setForm(prev => ({
      ...prev,
      eligible_incentive_types: prev.eligible_incentive_types.includes(t)
        ? prev.eligible_incentive_types.filter(x => x !== t)
        : [...prev.eligible_incentive_types, t],
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/sales/employees/${employee.id}/incentive-settings`, form,
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Incentive settings saved');
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-wt-surface border-wt-border max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-wt-text-primary">Incentive Settings — {employee?.full_name}</DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="py-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-wt-gold" /></div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-wt-paper rounded-lg border border-wt-border">
              <div>
                <p className="text-sm font-medium text-wt-text-primary">Incentive Enabled</p>
                <p className="text-xs text-wt-text-muted">Controls whether this employee can EARN incentives (separate from role permissions)</p>
              </div>
              <Switch checked={form.incentive_enabled} onCheckedChange={(v) => setForm(p => ({ ...p, incentive_enabled: v }))} data-testid="incentive-enabled-toggle" />
            </div>

            <div>
              <Label className="text-wt-text-primary text-sm">Eligible Incentive Types</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {TYPES.map(([val, label]) => (
                  <button key={val} type="button" onClick={() => toggleType(val)}
                    data-testid={`incentive-type-${val}`}
                    className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${form.eligible_incentive_types.includes(val)
                      ? 'bg-wt-gold/20 text-wt-gold border-wt-gold/50'
                      : 'bg-wt-bg text-wt-text-muted border-wt-border'}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-wt-text-primary text-sm">Effective From</Label>
                <DateInput  value={form.incentive_effective_from} onChange={(e) => setForm(p => ({ ...p, incentive_effective_from: e.target.value }))}
                  className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary" data-testid="incentive-effective-from" />
              </div>
              <div>
                <Label className="text-wt-text-primary text-sm">Effective To (optional)</Label>
                <DateInput  value={form.incentive_effective_to} onChange={(e) => setForm(p => ({ ...p, incentive_effective_to: e.target.value }))}
                  className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary" />
              </div>
            </div>

            <div>
              <Label className="text-wt-text-primary text-sm">Notes</Label>
              <Textarea value={form.incentive_notes} onChange={(e) => setForm(p => ({ ...p, incentive_notes: e.target.value }))}
                className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary" rows={2} />
            </div>
            <div>
              <Label className="text-wt-text-primary text-sm">Change Reason (audit)</Label>
              <Input value={form.change_notes} onChange={(e) => setForm(p => ({ ...p, change_notes: e.target.value }))}
                placeholder="Why is this being changed?" className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary" />
            </div>

            {settings && (
              <div className="text-xs text-wt-text-muted space-y-1 p-3 bg-wt-paper rounded-lg border border-wt-border">
                <p>Status: <Badge className={`text-xs border ${settings.incentive_enabled ? 'bg-green-500/20 text-green-300 border-green-500/40' : 'bg-red-500/15 text-red-300 border-red-500/40'}`}>{settings.current_status}</Badge></p>
                <p>Monthly target: {settings.current_month_target != null ? settings.current_month_target.toLocaleString() : 'Not set (company default applies)'}</p>
                {settings.incentive_updated_by && <p>Last updated by {settings.incentive_updated_by} on {new Date(settings.incentive_updated_at).toLocaleString()}</p>}
              </div>
            )}

            {audit.length > 0 && (
              <div className="max-h-32 overflow-y-auto space-y-1">
                <p className="text-xs font-medium text-wt-text-muted">Audit Trail</p>
                {audit.map(a => (
                  <div key={a.id} className="text-[11px] text-wt-text-muted border-l-2 border-wt-border pl-2">
                    {new Date(a.changed_at).toLocaleString()} — {a.changed_by_name}: enabled {String(a.old_value?.incentive_enabled ?? false)} → {String(a.new_value?.incentive_enabled)}{a.notes ? ` (${a.notes})` : ''}
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-wt-text-secondary">Cancel</Button>
              <Button onClick={save} disabled={saving} className="bg-wt-gold text-black hover:bg-wt-gold/90" data-testid="save-incentive-settings-btn">
                {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Save Settings
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EmployeeIncentiveSettingsDialog;
